import type { PhysicalCardLifecycleAction } from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycle.types'
import type { TranslateFn } from 'src/localization/translate'
import type { CardManagementLifecycleOperationStatus } from 'src/queries/physicalCardManagement/physicalCardManagement.models'

type PhysicalCardLifecycleTerminalOutcome = {
  message: string
  failureColorScheme: boolean
}

export const getPhysicalCardLifecycleTerminalOutcome = (
  action: PhysicalCardLifecycleAction,
  status: CardManagementLifecycleOperationStatus,
  translate: TranslateFn,
): PhysicalCardLifecycleTerminalOutcome => {
  if (status === 'SUCCEEDED') {
    if (action === 'replace') {
      return {
        message: translate(
          'Your replacement request was submitted. Your bank will be in touch shortly about issuing a replacement card.',
        ),
        failureColorScheme: false,
      }
    }
    if (action === 'lostOrStolen') {
      return {
        message: translate(
          'Your card was reported lost or stolen. Your bank will be in touch shortly about issuing a replacement card.',
        ),
        failureColorScheme: false,
      }
    }

    return {
      message: translate('${action} completed successfully.', {
        action: actionLabel(action, translate),
      }),
      failureColorScheme: false,
    }
  }
  if (status === 'CANCELED') {
    return {
      message: translate(
        '${action} was canceled. No changes were made to this card.',
        { action: actionLabel(action, translate) },
      ),
      failureColorScheme: false,
    }
  }
  return {
    message: translate(
      '${action} could not be completed. Review the card before trying again.',
      { action: actionLabel(action, translate) },
    ),
    failureColorScheme: true,
  }
}

const actionLabel = (
  action: PhysicalCardLifecycleAction,
  translate: TranslateFn,
): string => {
  switch (action) {
    case 'replace':
      return translate('Replacement request')
    case 'lostOrStolen':
      return translate('Lost or stolen report')
    case 'reportFraud':
      return translate('Fraud report')
    case 'cancel':
      return translate('Card cancellation')
    default:
      return assertUnreachable(action)
  }
}

const assertUnreachable = (action: never): never => {
  throw new Error(`Unexpected physical card lifecycle action: ${action}`)
}
